import { useCallback, useEffect, useState } from 'react';
import { useSignalR } from './useSignalR';
import { useEditorStore } from '../stores/editorStore';
import { useMovieStore } from '../stores/movieStore';
import type { SceneResponse, SceneEffectResponse } from '../types/api';
import type { SceneEffectType } from '../types/sceneEffects';
import {
  buildUpsertRequest,
  mapSceneEffectResponse,
  toSceneEffectResponse,
} from '../util/sceneEffectMappers';
import { mergeEffectParams, serializeEffectParams } from '../types/sceneEffects';

function useSceneEffectsActions() {
  const { currentScene, currentMovie } = useMovieStore();
  const { sceneEffects, setSceneEffects, upsertSceneEffect, removeSceneEffect } = useEditorStore();
  const {
    sceneEffectsGet,
    sceneEffectUpsertByType,
    sceneEffectPatchProperties,
    sceneEffectDelete,
    connect,
    status,
  } = useSignalR(false);

  const sceneId = currentScene?.id ? Number(currentScene.id) : null;
  const movieId = currentMovie?.id ? Number(currentMovie.id) : null;

  const ensureConnected = useCallback(async () => {
    if (status !== 'Connected') await connect();
  }, [status, connect]);

  const loadEffects = useCallback(async () => {
    if (!sceneId || !currentScene || Number.isNaN(sceneId) || !movieId) {
      setSceneEffects([]);
      return;
    }

    const sceneResponse: SceneResponse = {
      id: sceneId,
      movieId,
      name: currentScene.name,
      description: '',
      framesTotal: 0,
      sorter: currentScene.order ?? 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    await ensureConnected();
    const responses = await sceneEffectsGet(sceneResponse);
    const mapped = responses
      .map(mapSceneEffectResponse)
      .filter((effect): effect is NonNullable<typeof effect> => effect != null);
    setSceneEffects(mapped);
  }, [sceneId, currentScene, movieId, ensureConnected, sceneEffectsGet, setSceneEffects]);

  const toggleEffect = useCallback(
    async (effectType: SceneEffectType) => {
      if (!sceneId || !movieId || Number.isNaN(sceneId) || Number.isNaN(movieId)) return;

      const existing = sceneEffects.find((effect) => effect.effectType === effectType);
      const nextEnabled = existing ? !existing.isEnabled : true;
      const request = buildUpsertRequest(movieId, sceneId, effectType, nextEnabled, existing);

      await ensureConnected();
      const saved = await sceneEffectUpsertByType(request);
      const mapped = mapSceneEffectResponse(saved);
      if (mapped) upsertSceneEffect(mapped);
    },
    [sceneId, movieId, sceneEffects, ensureConnected, sceneEffectUpsertByType, upsertSceneEffect]
  );

  const patchEffect = useCallback(
    async (effectId: number, patch: Record<string, unknown>, isEnabled?: boolean) => {
      await ensureConnected();
      const saved = await sceneEffectPatchProperties({
        id: effectId,
        patch: JSON.stringify(patch),
        isEnabled,
      });
      const mapped = mapSceneEffectResponse(saved);
      if (mapped) upsertSceneEffect(mapped);
    },
    [ensureConnected, sceneEffectPatchProperties, upsertSceneEffect]
  );

  const clearEffects = useCallback(async () => {
    const enabled = sceneEffects.filter((effect) => effect.isEnabled);
    if (enabled.length === 0) return;

    await ensureConnected();
    await Promise.all(
      enabled.map(async (effect) => {
        const saved = await sceneEffectPatchProperties({
          id: effect.id,
          patch: '{}',
          isEnabled: false,
        });
        const mapped = mapSceneEffectResponse(saved);
        if (mapped) upsertSceneEffect(mapped);
      })
    );
  }, [sceneEffects, ensureConnected, sceneEffectPatchProperties, upsertSceneEffect]);

  const deleteEffect = useCallback(
    async (effectId: number) => {
      const existing = sceneEffects.find((effect) => effect.id === effectId);
      if (!existing) return;

      await ensureConnected();
      await sceneEffectDelete(toSceneEffectResponse(existing));
      removeSceneEffect(effectId);
    },
    [sceneEffects, ensureConnected, sceneEffectDelete, removeSceneEffect]
  );

  const applyEffectPayload = useCallback(
    async (effectType: SceneEffectType, action: string, params?: Record<string, unknown>) => {
      if (!sceneId || !movieId || Number.isNaN(sceneId) || Number.isNaN(movieId)) {
        throw new Error('No active scene');
      }

      const normalized = action.toLowerCase();
      const existing = sceneEffects.find((effect) => effect.effectType === effectType);

      if (normalized === 'disable') {
        if (!existing) return null;
        await ensureConnected();
        const saved = await sceneEffectPatchProperties({
          id: existing.id,
          patch: '{}',
          isEnabled: false,
        });
        const mapped = mapSceneEffectResponse(saved);
        if (mapped) upsertSceneEffect(mapped);
        return mapped;
      }

      const mergedParams = mergeEffectParams(effectType, {
        ...existing?.properties,
        ...(params ?? {}),
      });

      await ensureConnected();

      if (existing) {
        const saved = await sceneEffectPatchProperties({
          id: existing.id,
          patch: serializeEffectParams(mergedParams),
          isEnabled: true,
        });
        const mapped = mapSceneEffectResponse(saved);
        if (mapped) upsertSceneEffect(mapped);
        return mapped;
      }

      const request: SceneEffectResponse = buildUpsertRequest(movieId, sceneId, effectType, true);
      request.properties = serializeEffectParams(mergedParams);
      const saved = await sceneEffectUpsertByType(request);
      const mapped = mapSceneEffectResponse(saved);
      if (mapped) upsertSceneEffect(mapped);
      return mapped;
    },
    [
      sceneId,
      movieId,
      sceneEffects,
      ensureConnected,
      sceneEffectPatchProperties,
      sceneEffectUpsertByType,
      upsertSceneEffect,
    ]
  );

  return {
    sceneEffects,
    loadEffects,
    toggleEffect,
    patchEffect,
    clearEffects,
    deleteEffect,
    applyEffectPayload,
  };
}

/** Loads scene effects when the active scene changes. Call once (e.g. Stage). */
export function useSceneEffectsLoader() {
  const { loadEffects } = useSceneEffectsActions();
  const { currentScene } = useMovieStore();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void loadEffects()
      .catch((error) => console.error('[useSceneEffectsLoader] load failed', error))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadEffects, currentScene?.id]);

  return { loading, reloadEffects: loadEffects };
}

/** Scene effect mutations for toolbar / AI executor. Does not auto-load. */
export function useSceneEffectsSync() {
  return useSceneEffectsActions();
}
